// src/pages/StationFormPage.jsx
// Stations-Setup: Anschriften / Station / MS-Anlage / Trafos / Wandler / NSHV / Erderanlage / Thermografie

import { useState, useEffect, useRef } from 'react';
import { db, supabase, withTimeout, trafoBereinigen, konfigBereinigen, zeileBereinigen, geaenderteFelder } from '../lib/supabase';
import { useKunde } from '../hooks/useKunden';
import { useStation } from '../hooks/useStationen';
import { useVnbListe } from '../hooks/useVnb';
import { C, Field, TextInput, NumberInput, ComboInput, Select, Checkbox, Row, Subsection, DateInput, zurueckStil, BezeichnungVorschlaege } from './formui';
import { STATIONS_HERSTELLER, STATIONS_TYPEN, BAUFORM, BAUFORM_WERTE, RAUM_FELDER, RAUM_ANZAHL } from '../lib/auswahllisten';
import { STATIONS_TYPEN_JE_HERSTELLER, stationsTypenFuer, MS_BETRIEBSSPANNUNG_KV, isolierartFuerMsTyp } from '../lib/typenlisten';
import ErderanlageBlock from './ErderanlageBlock';
import TrafosEditor from './TrafosEditor';
import MsFelderEditor, { HerstellerPicker, MS_HERSTELLER_VORSCHLAEGE } from './MsFelderEditor';
import WandlerEditor from './WandlerEditor';
import ZaehlerEditor from './ZaehlerEditor';
import VnbPicker from './VnbPicker';
import VertraegeEditor from './VertraegeEditor';
import { useAuth } from '../hooks/useAuth';

const ISOLIERART = [
  // Reihenfolge und Texte: Anna-Lena 18.09.2026. Nur bei offener Bauweise passt
  // die Fachbroschuere 01.
  { value: 'luft_offen', label: 'Luft, offene Bauweise ohne Berührungsschutz' },
  { value: 'luft', label: 'Luft (gekapselt)' },
  { value: 'sf6', label: 'SF6-Gas' },
  { value: 'clean_air', label: 'Clean Air' },
];
const VERRECHNUNG = [
  { value: 'ms', label: 'MS-Messung' },
  { value: 'ns', label: 'NS-Messung' },
  { value: 'keine', label: 'Keine Verrechnungsmessung' },
];
// Betriebsspannungen (nicht Bemessungsspannungen 24/36 - die stehen auf dem
// Typenschild, gefragt ist hier das Netz). Liste aus lib/typenlisten.js.
const SPANNUNG_OPTIONEN = MS_BETRIEBSSPANNUNG_KV;
const STATIONS_HERSTELLER_ALLE = [...new Set([...STATIONS_HERSTELLER, ...STATIONS_TYPEN_JE_HERSTELLER.map(e => e.hersteller)])];
// Wartungszyklus nur 1-4 Jahre (Anna-Lena 18.09.2026: "5 Jahre gibt es nicht").
const WARTUNGSZYKLUS = [
  { value: '1-jaehrig', label: '1-jährig' },
  { value: '2-jaehrig', label: '2-jährig' },
  { value: '3-jaehrig', label: '3-jährig' },
  { value: '4-jaehrig', label: '4-jährig' },
  { value: 'kein_zyklus_bekannt', label: 'Kein Zyklus bekannt' },
  { value: 'keine_angaben', label: 'Keine Angaben' },
];

// Reiter NSHV und Thermografie 14.05.2026 entfernt: NSHV/Thermografie werden in der
// Wartung selbst gefuellt. Die drei Haekchen daraus (NSHV, Oelwanne, Thermografie)
// stehen seit 20.09.2026 im Reiter "Station" unter "Ausstattung der Station" -
// vorher waren sie ueberhaupt nicht mehr einstellbar.
const TABS = [
  { key: 'anschrift',   label: 'Anschriften' },
  { key: 'station',     label: 'Station' },
  { key: 'msanlage',    label: 'MS-Anlage' },
  { key: 'trafos',      label: 'Trafos' },
  { key: 'wandler',     label: 'Wandler / Zähler' },
  { key: 'erderanlage', label: 'Erderanlage' },
];
// Vertraege (Migration 45) nur fuer Buero und Admin - dort stehen Preise.
const TAB_VERTRAEGE = { key: 'vertraege', label: 'Verträge' };

const DEFAULT_STAMM = {
  bezeichnung: '', stations_nr: '',
  standort_strasse: '', standort_plz: '', standort_ort: '',
  typ: '', baujahr: null, wartungs_zyklus: '',
  letzte_wartung: '',
  gps_lat: null, gps_lng: null, notizen: '',
  // Objekt-Anschrift (abweichend vom Auftraggeber)
  objekt_abweichend: false,
  objekt_name: '', objekt_strasse: '', objekt_plz: '', objekt_ort: '',
  objekt_telefon: '', objekt_ansprechpartner: '', objekt_position: '',
  objekt_telefon_durchwahl: '', objekt_mobil: '', objekt_email: '',
  // Ansprechpartner vor Ort
  vor_ort_ansprechpartner: '', vor_ort_position: '',
  vor_ort_telefon: '', vor_ort_mobil: '', vor_ort_email: '',
  // VNB
  vnb_id: null, vnb_stationsbezeichnung: '',
};

const DEFAULT_CONFIG = {
  stations_typ: '', stations_hersteller: '', stations_modelltyp: '',
  stations_seriennummer: '', stations_baujahr: null,
  ms_anlage_vorhanden: true, ms_anlage_hersteller: '',
  ms_anlage_anzahl_felder: null, ms_anlage_betriebsspannung_kv: null,
  ms_anlage_isolierart: '',
  nshv_vorhanden: true, verrechnungsmessung: 'keine',
  stromwandler_vorhanden: false, spannungswandler_vorhanden: false,
  erderanlage_vorhanden: true, oelauffangwanne_vorhanden: false,
  elektrothermografie_durchgef: false, notizen: '',
  // Migration 76 (08.10.2026)
  zaehler: [], nshv_bezeichnung: '', nshv_verteilungen: [],
};

// --- HELPER: Diff-basiertes Speichern für Collections (MS-Felder, Trafos, Wandler) -----
// Statt brutal alle bestehenden Items zu löschen und neu einzufügen (Datenverlust-Risiko
// bei Crash + sehr viele HTTP-Requests), machen wir:
//   1) Bestehende IDs aus DB laden
//   2) Bulk-Upsert aller neuen Items (eine einzige Anfrage)
//   3) Nur die Items löschen, die nicht mehr in der neuen Liste sind
// Alle Operationen parallel und mit Timeout — kein Endlos-Hänger mehr möglich.
//
// Gesamtpruefung 11.09.2026 - vier Fehler, die hier zusammenkamen:
//  - Alte und neue Zeilen gingen in EINER Anfrage raus. Die Bibliothek setzt
//    dann bei den neuen Zeilen alle Spalten, die nur die alten haben (id,
//    active, created_at), auf NULL - die Datenbank lehnte alles ab. Folge:
//    Zu einer Station mit einem Trafo liess sich kein zweiter hinzufuegen.
//  - Die gespeicherten IDs kamen nie zurueck ins Formular. Ein zweites
//    Speichern legte neue Zeilen noch einmal an (Doppelte) bzw. loeschte und
//    ersetzte sie (neue IDs - Protokoll-Kapitel verloren ihren Trafo).
//  - Entfernte Trafos wurden endgueltig geloescht (fuer Monteure verboten, das
//    scheiterte lautlos) statt stillgelegt.
//  - Speichern und Loeschen liefen gleichzeitig; scheiterte das Speichern,
//    wurde trotzdem geloescht.
// Jetzt: nacheinander - bestehende aktualisieren, Entferntes stilllegen, dann
// Neues anlegen. Rueckgabe: die gespeicherten Zeilen MIT IDs, in Reihenfolge.
//
// Seit v63 (Gesamtpruefung 15.09.2026) mit "vorher" (die beim Oeffnen geladenen
// Zeilen): Bestehende Zeilen bekommen NUR ihre geaenderten Felder, und
// entfernt wird nur, was in DIESEM Formular entfernt wurde. Vorher gingen alle
// Spalten zurueck (eine inzwischen im Protokoll korrigierte Seriennummer war
// wieder alt) und JEDE aktive Zeile, die nicht im Formular stand, wurde
// stillgelegt - auch ein Trafo, den der Monteur gerade nachgetragen hatte.
async function saveCollectionDiff(table, stationId, newItems, label, vorherZeilen) {
  const hasActiveColumn = table === 'station_ms_felder' || table === 'station_trafos' || table === 'station_wandler';
  const bleiben = new Set(newItems.filter(i => i.id).map(i => i.id));
  let zuEntfernen;
  const vorherNachId = vorherZeilen ? new Map(vorherZeilen.filter(z => z.id).map(z => [z.id, z])) : null;
  if (vorherNachId) {
    zuEntfernen = [...vorherNachId.keys()].filter(id => !bleiben.has(id));
  } else {
    // Neue Station: noch nichts geladen - alle bestehenden zaehlen.
    let query = supabase.from(table).select('id').eq('station_id', stationId);
    if (hasActiveColumn) query = query.eq('active', true);
    const existingRes = await withTimeout(query, 20000, label + ' (bestehende laden)');
    if (existingRes.error) throw existingRes.error;
    zuEntfernen = (existingRes.data || []).map(r => r.id).filter(id => !bleiben.has(id));
  }

  const pruefen = (r) => {
    if (r.error) {
      // Kennt die Datenbank ein Feld nicht, lehnt sie den GANZEN Datensatz ab.
      const fehlt = String(r.error.message || '').match(/Could not find the '([^']+)' column/);
      if (fehlt) {
        throw new Error(label + ' wurden NICHT gespeichert: Die Datenbank kennt das Feld „'
          + fehlt[1] + '" nicht. Bitte Anna-Lena bzw. Claude Bescheid geben — '
          + 'es fehlt eine Migration. Deine Eingaben stehen noch im Formular.');
      }
      throw new Error(label + ': ' + r.error.message);
    }
    return r.data || [];
  };

  // 2) Bestehende aktualisieren. Sie kommen alle aus select('*') und haben
  //    deshalb dieselben Spalten.
  const alt = newItems.filter(i => i.id);
  const neu = newItems.filter(i => !i.id);
  let gespeichert = [];
  if (alt.length > 0 && vorherNachId) {
    for (const item of alt) {
      const vorher = vorherNachId.get(item.id);
      const { id, ...rest } = item;
      const patch = vorher ? geaenderteFelder(rest, vorher) : rest;
      if (Object.keys(patch).length === 0) { gespeichert.push(item); continue; }
      const rows = pruefen(await withTimeout(
        supabase.from(table).update(patch).eq('id', id).select(), 20000, label + ' speichern'));
      if (rows.length === 0) {
        throw new Error(label + ': eine Zeile wurde nicht gespeichert (gelöscht oder keine Berechtigung).');
      }
      gespeichert.push(rows[0]);
    }
  } else if (alt.length > 0) {
    gespeichert = pruefen(await withTimeout(
      supabase.from(table).upsert(alt, { defaultToNull: false }).select(),
      30000, label + ' speichern'));
  }

  // 3) Entferntes VOR dem Anlegen: Scheitert es, wird nichts doppelt angelegt.
  if (zuEntfernen.length > 0) {
    if (hasActiveColumn) {
      // Stilllegen statt loeschen: An Trafos und Feldern haengen Protokolle.
      const r = await withTimeout(
        supabase.from(table).update({ active: false }).in('id', zuEntfernen),
        20000, label + ' entfernen');
      if (r.error) throw new Error(label + ' konnten nicht entfernt werden: ' + r.error.message);
    } else {
      const r = await withTimeout(
        supabase.from(table).delete().in('id', zuEntfernen).select('id'),
        20000, label + ' entfernen');
      if (r.error) throw new Error(label + ' konnten nicht entfernt werden: ' + r.error.message);
      if ((r.data || []).length < zuEntfernen.length) {
        throw new Error(label + ': Entfernen darf nur das Büro. Bitte dort Bescheid geben — '
          + 'alles andere wurde gespeichert.');
      }
    }
  }

  // 4) Neue anlegen - eigene Anfrage, fehlende Spalten bekommen ihren Standardwert.
  if (neu.length > 0) {
    gespeichert = gespeichert.concat(pruefen(await withTimeout(
      supabase.from(table).insert(neu, { defaultToNull: false }).select(),
      30000, label + ' anlegen')));
  }
  return gespeichert.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
}

// NSHV und weitere Verteilungen (Anna-Lena 08.10.2026, Migration 76): Jede
// Verteilung bekommt im Wartungsprotokoll ein eigenes NSHV-Kapitel mit allen
// Pruefpunkten. Gilt fuer NEU angelegte Wartungen - in einer laufenden traegt
// der Monteur sie im NSHV-Kapitel nach.
function neueVerteilungsId() {
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
  });
}
function VerteilungenEditor({ config, updateConf }) {
  const liste = Array.isArray(config.nshv_verteilungen) ? config.nshv_verteilungen : [];
  const setze = (neu) => updateConf('nshv_verteilungen', neu);
  return (
    <div style={{ margin: '8px 0 4px 30px', padding: 12, background: C.gray100, borderRadius: 8 }}>
      <Field label='Bezeichnung der NSHV' hint='optional, z. B. "NSHV" oder "Verteilung 1"'>
        <TextInput value={config.nshv_bezeichnung} onChange={v => updateConf('nshv_bezeichnung', v)}/>
      </Field>
      {liste.map((v, i) => (
        <div key={v.id || i} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 8 }}>
          <div style={{ flex: 1 }}>
            <Field label={'Weitere Verteilung ' + (i + 2)}>
              <TextInput value={v.bezeichnung} placeholder='z. B. VT 2 Keller'
                onChange={txt => setze(liste.map((x, k) => (k === i ? { ...x, bezeichnung: txt } : x)))}/>
            </Field>
          </div>
          <button type='button' onClick={() => setze(liste.filter((_, k) => k !== i))}
            style={{ minHeight: 44, marginBottom: 14, padding: '8px 14px', borderRadius: 8, border: '1.5px solid ' + C.gray200,
                     background: C.white, color: C.gray700, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
            Entfernen
          </button>
        </div>
      ))}
      <button type='button' onClick={() => setze([...liste, { id: neueVerteilungsId(), bezeichnung: '' }])}
        style={{ marginTop: 6, minHeight: 44, padding: '9px 16px', borderRadius: 8, border: '2px dashed ' + C.gray300,
                 background: C.white, color: C.navy, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
        + weitere Verteilung
      </button>
      <div style={{ fontSize: 13, color: C.gray500, marginTop: 8, lineHeight: 1.4 }}>
        Jede Verteilung bekommt im Wartungsprotokoll ein eigenes NSHV-Kapitel mit allen Prüfpunkten.
      </div>
    </div>
  );
}

export default function StationFormPage({ kundeId, stationId, onCancel, onSaved, startTab }) {
  const { isBuero } = useAuth();
  const vertraegeBesucht = useRef(false);
  const vertraegeUngespeichert = useRef(false);
  const vertraegeVerwerfenOk = (text) => !vertraegeUngespeichert.current
    || window.confirm(text || 'Es gibt ungespeicherte Änderungen an den Verträgen (grüner Knopf „Verträge speichern“). Trotzdem schließen?');
  const abbrechen = () => { if (vertraegeVerwerfenOk()) onCancel(); };
  const [internalStationId, setInternalStationId] = useState(stationId || null);
  const isEditMode = !!internalStationId;
  const stationData = useStation(isEditMode ? internalStationId : null);
  const effectiveKundeId = kundeId || stationData?.station?.kunde_id;
  const { kunde, loading: kundeLoading } = useKunde(effectiveKundeId);
  const { vnben, reload: reloadVnben } = useVnbListe();

  const [activeTab, setActiveTab] = useState(startTab || 'anschrift');
  if (activeTab === 'vertraege') vertraegeBesucht.current = true;
  const [stammdaten, setStammdaten] = useState({ ...DEFAULT_STAMM });
  const [config, setConfig] = useState({ ...DEFAULT_CONFIG });
  const [msFelder, setMsFelder] = useState([]);
  const [trafos, setTrafos] = useState([]);
  const [wandler, setWandler] = useState([]);
  const [dataPopulated, setDataPopulated] = useState(!stationId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(null);
  // Stand beim Oeffnen bzw. nach dem letzten Speichern. Gespeichert werden nur
  // Abweichungen davon - so ueberschreibt das Formular keine Aenderungen, die
  // inzwischen im Protokoll gemacht wurden.
  const vorher = useRef(null);

  useEffect(() => {
    if (!stationId || dataPopulated || !stationData.station) return;
    const s = stationData.station;
    const geladeneStammdaten = ({
      // Der VNB-Name stand bei importierten Stationen nur in
      // vnb_stationsbezeichnung - das Pflichtfeld wirkte dort leer.
      bezeichnung: s.bezeichnung || '', stations_nr: s.stations_nr || s.vnb_stationsbezeichnung || '',
      standort_strasse: s.standort_strasse || '', standort_plz: s.standort_plz || '', standort_ort: s.standort_ort || '',
      // Import schrieb "3-jährig", die Auswahl kennt "3-jaehrig".
      typ: s.typ || '', baujahr: s.baujahr, wartungs_zyklus: (s.wartungs_zyklus || '').replace('jährig', 'jaehrig'),
      letzte_wartung: s.letzte_wartung || '',
      gps_lat: s.gps_lat, gps_lng: s.gps_lng, notizen: s.notizen || '',
      objekt_abweichend: s.objekt_abweichend || false,
      objekt_name: s.objekt_name || '', objekt_strasse: s.objekt_strasse || '',
      objekt_plz: s.objekt_plz || '', objekt_ort: s.objekt_ort || '',
      objekt_telefon: s.objekt_telefon || '', objekt_ansprechpartner: s.objekt_ansprechpartner || '',
      objekt_position: s.objekt_position || '', objekt_telefon_durchwahl: s.objekt_telefon_durchwahl || '',
      objekt_mobil: s.objekt_mobil || '', objekt_email: s.objekt_email || '',
      vor_ort_ansprechpartner: s.vor_ort_ansprechpartner || '', vor_ort_position: s.vor_ort_position || '',
      vor_ort_telefon: s.vor_ort_telefon || '', vor_ort_mobil: s.vor_ort_mobil || '', vor_ort_email: s.vor_ort_email || '',
      vnb_id: s.vnb_id || null, vnb_stationsbezeichnung: s.vnb_stationsbezeichnung || '',
    });
    setStammdaten(geladeneStammdaten);
    let geladeneConfig = null;
    if (stationData.config) {
      const c = stationData.config;
      geladeneConfig = ({
        stations_typ: c.stations_typ || '', stations_hersteller: c.stations_hersteller || '',
        stations_modelltyp: c.stations_modelltyp || '', stations_seriennummer: c.stations_seriennummer || '',
        stations_baujahr: c.stations_baujahr, ms_anlage_vorhanden: c.ms_anlage_vorhanden ?? true,
        ms_anlage_hersteller: c.ms_anlage_hersteller || '', ms_anlage_anzahl_felder: c.ms_anlage_anzahl_felder,
        ms_anlage_betriebsspannung_kv: c.ms_anlage_betriebsspannung_kv, ms_anlage_isolierart: c.ms_anlage_isolierart || '',
        nshv_vorhanden: c.nshv_vorhanden ?? true, verrechnungsmessung: c.verrechnungsmessung || 'keine',
        stromwandler_vorhanden: c.stromwandler_vorhanden ?? false, spannungswandler_vorhanden: c.spannungswandler_vorhanden ?? false,
        erderanlage_vorhanden: c.erderanlage_vorhanden ?? true, oelauffangwanne_vorhanden: c.oelauffangwanne_vorhanden ?? false,
        elektrothermografie_durchgef: c.elektrothermografie_durchgef ?? false, notizen: c.notizen || '',
        erder_grenzwert_ohm: c.erder_grenzwert_ohm ?? null,
        anzahl_ms_raeume: c.anzahl_ms_raeume ?? null, anzahl_ns_raeume: c.anzahl_ns_raeume ?? null,
        anzahl_ms_ns_raeume: c.anzahl_ms_ns_raeume ?? null, anzahl_trafokammern: c.anzahl_trafokammern ?? null,
        ms_erdungsschalter: c.ms_erdungsschalter ?? null,
        zaehler: Array.isArray(c.zaehler) ? c.zaehler : [],
        nshv_bezeichnung: c.nshv_bezeichnung || '',
        nshv_verteilungen: Array.isArray(c.nshv_verteilungen) ? c.nshv_verteilungen : [],
      });
      setConfig(geladeneConfig);
    }
    // Steht die Bauform nur im alten Feld (stationen.typ), sie hierher holen,
    // damit sie beim Speichern im richtigen Feld landet.
    if (!stationData.config?.stations_typ && BAUFORM_WERTE.includes(s.typ)) {
      setConfig(c => ({ ...c, stations_typ: s.typ }));
    }
    setMsFelder(stationData.msFelder || []);
    setTrafos(stationData.trafos || []);
    setWandler(stationData.wandler || []);
    vorher.current = {
      stammdaten: geladeneStammdaten,
      config: geladeneConfig,           // null = es gibt noch keine Konfiguration
      msFelder: stationData.msFelder || [],
      trafos: stationData.trafos || [],
      wandler: stationData.wandler || [],
    };
    setDataPopulated(true);
  }, [stationId, dataPopulated, stationData.station, stationData.config, stationData.msFelder, stationData.trafos, stationData.wandler]);

  // 15.05. (#34): Beim Anlegen einer Station Adresse vom Kunden vorbefuellen.
  // Nur wenn neues Anlegen UND noch keine Adresse eingetragen ist. Editierbar bleibt es.
  useEffect(() => {
    if (isEditMode || !kunde) return;
    setStammdaten(s => {
      // Nur ueberschreiben wenn alle drei Felder noch leer sind (User hat noch nichts getippt).
      if (s.standort_strasse || s.standort_plz || s.standort_ort) return s;
      return {
        ...s,
        standort_strasse: kunde.strasse || '',
        standort_plz: kunde.plz || '',
        standort_ort: kunde.ort || '',
      };
    });
  }, [kunde, isEditMode]);

  const updateStamm = (k, v) => setStammdaten(s => ({ ...s, [k]: v }));
  const updateConf = (k, v) => setConfig(c => ({ ...c, [k]: v }));

  const handleUseLocation = () => {
    if (!navigator.geolocation) { setError('Browser unterstützt keine Standort-Abfrage.'); return; }
    setProgress('Frage Standort ab... Bitte im Browser erlauben.');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        updateStamm('gps_lat', Number(pos.coords.latitude.toFixed(6)));
        updateStamm('gps_lng', Number(pos.coords.longitude.toFixed(6)));
        setProgress('Standort übernommen (Genauigkeit: ' + Math.round(pos.coords.accuracy) + ' m)');
        setTimeout(() => setProgress(null), 3000);
      },
      (err) => { setProgress(null); setError('Standort nicht verfügbar: ' + err.message); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleSave = async (closeAfter = true) => {
    setError(null); setProgress(null);
    // 14.05.: Stationsname-VNB ist jetzt Primaer-Identifier. Mindestens einer von
    // "Stationsname bei VNB" oder "Bezeichnung" muss ausgefuellt sein.
    const stationsNrTrim = (stammdaten.stations_nr || '').trim();
    const bezeichnungTrim = (stammdaten.bezeichnung || '').trim();
    if (!stationsNrTrim && !bezeichnungTrim) {
      setError('Bitte mindestens den Stationsnamen beim VNB oder eine Bezeichnung eingeben.');
      setActiveTab('station'); return;
    }
    // Wenn nur stations_nr ohne bezeichnung: stations_nr als bezeichnung verwenden
    // (DB-Constraint: bezeichnung kann noch NOT NULL sein).
    if (saving) return;
    setSaving(true);
    // Nach "Fertigstellen" bleibt der Knopf bis zum Seitenwechsel gesperrt -
    // vorher war er 0,5 s wieder frei und ein zweiter Tipp speicherte erneut.
    let schliesst = false;
    // Station-Zeile aus einem Formularstand - auch fuer den Stand "vorher".
    const stationZeile = (stammdaten, config) => {
      const nr = (stammdaten.stations_nr || '').trim();
      return {
        kunde_id: effectiveKundeId,
        bezeichnung: (stammdaten.bezeichnung || '').trim() || nr,
        stations_nr: nr || null,
        standort_strasse: stammdaten.standort_strasse || null,
        standort_plz: stammdaten.standort_plz || null,
        standort_ort: stammdaten.standort_ort || null,
        // stationen.typ wird nur noch mitgefuehrt - massgeblich ist config.stations_typ.
        typ: config.stations_typ || stammdaten.typ || null, baujahr: stammdaten.baujahr,
        wartungs_zyklus: stammdaten.wartungs_zyklus || null,
        letzte_wartung: stammdaten.letzte_wartung || null,
        gps_lat: stammdaten.gps_lat, gps_lng: stammdaten.gps_lng,
        notizen: stammdaten.notizen || null, active: true,
        objekt_abweichend: !!stammdaten.objekt_abweichend,
        objekt_name: stammdaten.objekt_name || null,
        objekt_strasse: stammdaten.objekt_strasse || null,
        objekt_plz: stammdaten.objekt_plz || null,
        objekt_ort: stammdaten.objekt_ort || null,
        objekt_telefon: stammdaten.objekt_telefon || null,
        objekt_ansprechpartner: stammdaten.objekt_ansprechpartner || null,
        objekt_position: stammdaten.objekt_position || null,
        objekt_telefon_durchwahl: stammdaten.objekt_telefon_durchwahl || null,
        objekt_mobil: stammdaten.objekt_mobil || null,
        objekt_email: stammdaten.objekt_email || null,
        vor_ort_ansprechpartner: stammdaten.vor_ort_ansprechpartner || null,
        vor_ort_position: stammdaten.vor_ort_position || null,
        vor_ort_telefon: stammdaten.vor_ort_telefon || null,
        vor_ort_mobil: stammdaten.vor_ort_mobil || null,
        vor_ort_email: stammdaten.vor_ort_email || null,
        vnb_id: stammdaten.vnb_id || null,
        // Dasselbe wie "Stationsname bei VNB": PDF, Planung und Kalender lesen
        // dieses Feld, das Formular schrieb bisher nur stations_nr.
        vnb_stationsbezeichnung: nr || stammdaten.vnb_stationsbezeichnung || null,
      };
    };
    try {
      setProgress('Speichere Station...');
      const payload = stationZeile(stammdaten, config);
      let finalId;
      if (isEditMode && vorher.current) {
        // Bestehende Station: nur Geaendertes (siehe "vorher").
        const patch = geaenderteFelder(zeileBereinigen(payload),
          zeileBereinigen(stationZeile(vorher.current.stammdaten, vorher.current.config || {})));
        if (Object.keys(patch).length > 0) {
          const { data: rows, error: e1 } = await withTimeout(
            supabase.from('stationen').update(patch).eq('id', internalStationId).select('id'), 15000, 'Station speichern');
          if (e1) throw new Error('Station: ' + (e1.message || JSON.stringify(e1)));
          if (!rows || rows.length === 0) throw new Error('Station wurde nicht gespeichert (gelöscht oder keine Berechtigung).');
        }
        finalId = internalStationId;
      } else {
        const neu = zeileBereinigen(payload);
        if (isEditMode) neu.id = internalStationId;
        const { data: station, error: e1 } = await withTimeout(
          db.stationen.save(neu), 15000, 'Station speichern'
        );
        if (e1) throw new Error('Station: ' + (e1.message || JSON.stringify(e1)));
        if (!station?.id) throw new Error('Keine Station-ID zurück. RLS-Problem?');
        finalId = station.id;
      }

      // SOFORT merken, dass die Station jetzt existiert - noch bevor Konfiguration
      // und Anlagenteile gespeichert werden.
      //
      // Vorher stand das ganz am Ende. Scheiterte einer der folgenden Schritte,
      // sprang der Ablauf in den Fehlerzweig und die ID wurde nie gemerkt. Das
      // Formular blieb im Zustand "neue Station" - und der naechste Versuch legte
      // die Station NOCH EINMAL an. Am 04.09.2026 sind so acht Kopien derselben
      // Station entstanden, alle ohne Konfiguration und ohne Trafo.
      setInternalStationId(finalId);

      // --- Anlagenteile PARALLEL speichern (statt seriell wie früher) ----------
      // Verwendet diff-basierten Upsert: keine destruktiven Delete+Insert mehr,
      // bestehende IDs werden weitergenutzt → kein Datenverlust bei Crash.
      setProgress('Speichere Konfiguration und Anlagenteile...');

      // Konfiguration (eigenständig, parallel)
      //
      // Leere Auswahlfelder MUESSEN als NULL rausgehen. Die Datenbank laesst
      // bei stations_typ, ms_anlage_isolierart und verrechnungsmessung nur die
      // festgelegten Werte oder NULL zu - ein leerer Text ist keines von beidem
      // und wird abgelehnt ("violates check constraint"). Das Formular startet
      // diese Felder aber mit ''. Wer die Bauform nicht ausgewaehlt hat, konnte
      // seine Konfiguration deshalb nicht speichern; bis zum 04.09.2026 stand
      // der Fehler nur in der Browser-Konsole.
      // (Bereinigung zentral in konfigBereinigen.) Gibt es schon eine
      // Konfiguration, gehen nur geaenderte Felder raus.
      // Die Haekchen "Strom-/Spannungswandler vorhanden" gibt es im Formular
      // nicht mehr (18.09.2026). Sie folgen jetzt der Wandler-Liste - sonst
      // blieben sie fuer immer auf "ja", auch wenn kein Wandler mehr da ist.
      const configFertig = { ...config,
        stromwandler_vorhanden: wandler.some(w => (w.wandler_typ || 'strom') === 'strom' && w.active !== false),
        spannungswandler_vorhanden: wandler.some(w => w.wandler_typ === 'spannung' && w.active !== false) };
      const konfigVorher = vorher.current?.config;
      const konfigPatch = konfigVorher
        ? geaenderteFelder(konfigBereinigen(configFertig), konfigBereinigen(konfigVorher)) : null;
      const konfigPromise = (konfigPatch && Object.keys(konfigPatch).length === 0
        ? Promise.resolve({ error: null })
        : konfigPatch
          ? withTimeout(supabase.from('station_konfiguration').update(konfigPatch).eq('station_id', finalId).select('station_id'),
              15000, 'Konfiguration speichern')
          : (async () => {
              // Beim Oeffnen gab es keine Konfiguration. Hat sie inzwischen jemand
              // angelegt (Monteur im Protokoll), nur die hier geaenderten Felder
              // senden - sonst stuenden seine Angaben wieder auf Standard.
              const { data: jetzt } = await withTimeout(
                supabase.from('station_konfiguration').select('station_id').eq('station_id', finalId).maybeSingle(),
                15000, 'Konfiguration prüfen');
              if (jetzt) {
                const patch = geaenderteFelder(konfigBereinigen(configFertig), konfigBereinigen({ ...DEFAULT_CONFIG }));
                if (Object.keys(patch).length === 0) return { error: null };
                return withTimeout(supabase.from('station_konfiguration').update(patch).eq('station_id', finalId).select('station_id'),
                  15000, 'Konfiguration speichern');
              }
              return withTimeout(db.stationKonfig.save({ ...configFertig, station_id: finalId }), 15000, 'Konfiguration speichern');
            })()
      ).then(r => {
        // Frueher nur eine Konsolen-Warnung: Die Station wurde als gespeichert
        // gemeldet, MS-Anlage, Isolierart und Ausstattung waren aber weg.
        // So etwas faellt erst Wochen spaeter auf. Jetzt bricht es sichtbar ab.
        if (r.error) throw new Error('Konfiguration: ' + r.error.message);
        return r;
      });

      // Bereite items normalisiert vor: Positionen neu, Zeitstempel raus (setzt
      // die Datenbank), Trafo-Zahlenfelder als Zahl. id nur, wenn vorhanden -
      // sonst vergibt die Datenbank eine neue.
      const normItems = (arr, bereinigen = (x) => x) => arr.map((item, i) => {
        const { id, created_at, updated_at, ...rest } = item || {};
        const data = bereinigen({ ...rest, station_id: finalId, position: i });
        if (id) data.id = id;
        return data;
      });

      const v = vorher.current;
      const msPromise = saveCollectionDiff('station_ms_felder', finalId, normItems(msFelder, zeileBereinigen), 'MS-Felder',
        v ? normItems(v.msFelder, zeileBereinigen) : null);
      const trafosPromise = saveCollectionDiff('station_trafos', finalId, normItems(trafos, trafoBereinigen), 'Trafos',
        v ? normItems(v.trafos, trafoBereinigen) : null);
      const wandlerPromise = saveCollectionDiff('station_wandler', finalId, normItems(wandler, zeileBereinigen), 'Wandler',
        v ? normItems(v.wandler, zeileBereinigen) : null);

      // Parallel, aber jedes Ergebnis einzeln auswerten: Was gespeichert ist,
      // kommt MIT seinen IDs zurueck ins Formular - auch wenn ein anderer Teil
      // scheitert. Sonst legte der naechste Versuch es doppelt an.
      const ergebnisse = await Promise.allSettled([konfigPromise, msPromise, trafosPromise, wandlerPromise]);
      const [rKonfig, rMs, rTrafos, rWandler] = ergebnisse;
      if (rMs.status === 'fulfilled') setMsFelder(rMs.value);
      if (rTrafos.status === 'fulfilled') setTrafos(rTrafos.value);
      if (rWandler.status === 'fulfilled') setWandler(rWandler.value);
      // Neuer Stand "vorher": was jetzt gespeichert ist. Gescheiterte Teile
      // behalten ihren alten Vergleichsstand und werden beim naechsten Mal erneut gesendet.
      const altV = vorher.current || { stammdaten: null, config: null, msFelder: [], trafos: [], wandler: [] };
      vorher.current = {
        stammdaten: { ...stammdaten },
        config: rKonfig.status === 'fulfilled' ? { ...config } : altV.config,
        msFelder: rMs.status === 'fulfilled' ? rMs.value : altV.msFelder,
        trafos: rTrafos.status === 'fulfilled' ? rTrafos.value : altV.trafos,
        wandler: rWandler.status === 'fulfilled' ? rWandler.value : altV.wandler,
      };
      const fehlschlag = ergebnisse.find(r => r.status === 'rejected');
      if (fehlschlag) throw fehlschlag.reason;

      if (closeAfter) {
        setProgress('Erfolgreich gespeichert!');
        if (onSaved && vertraegeVerwerfenOk('Die Station ist gespeichert – die Änderungen an den Verträgen aber NICHT (grüner Knopf „Verträge speichern“). Trotzdem schließen?')) {
          schliesst = true; setTimeout(() => onSaved(finalId), 500);
        }
      } else {
        setProgress('Zwischengespeichert. Du kannst weiter bearbeiten.');
        setTimeout(() => setProgress(null), 3000);
      }
    } catch (e) {
      console.error('[ETB Save] FEHLER:', e);
      setError(e.message || 'Fehler beim Speichern. Console (F12) prüfen.');
    } finally {
      if (!schliesst) setSaving(false);
    }
  };

  if (stationId && !dataPopulated && stationData.loading) {
    return <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div style={{ textAlign: 'center', color: C.gray500 }}>Lade Stationsdaten...</div></div>;
  }
  // Laden gescheitert: KEIN leeres Formular. Speichern haette sonst die
  // Station mit Leerem ueberschrieben und alle Trafos entfernt.
  if (stationId && !dataPopulated) {
    return (
      <div style={{ minHeight: '100vh', background: C.bg, padding: 32, textAlign: 'center' }}>
        <div style={{ color: C.red, marginBottom: 16, fontSize: 15 }}>
          Die Station konnte nicht vollständig geladen werden{stationData.error ? ': ' + stationData.error : '.'}
        </div>
        <button onClick={stationData.reload} style={{ padding: '10px 18px', background: C.green, color: C.white, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 15, marginRight: 10 }}>Nochmal versuchen</button>
        <button onClick={abbrechen} style={{ padding: '10px 18px', background: C.gray100, color: C.navy, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 15 }}>Zurück</button>
      </div>
    );
  }

  const tabBg     = (k) => activeTab === k ? C.yellowBg : 'transparent';
  const tabColor  = (k) => activeTab === k ? C.navy : C.gray500;
  const tabBorder = (k) => activeTab === k ? '3px solid ' + C.yellow : '3px solid transparent';
  const tabWeight = (k) => activeTab === k ? 800 : 600;

  // Aktuell ausgewaehlter VNB - fuer Anzeige des Ansprechpartners
  const selectedVnb = (vnben || []).find(v => v.id === stammdaten.vnb_id);

  return (
    <div style={{ background: C.bg, minHeight: '100vh', fontFamily: "system-ui,-apple-system,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif" }}>
      <div style={{ background: C.navy, padding: '14px', boxShadow: '0 2px 14px rgba(0,0,0,0.22)' }}>
        <div style={{ maxWidth: 1400, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => { if (vertraegeUngespeichert.current && !window.confirm('Es gibt ungespeicherte Änderungen an den Verträgen. Trotzdem zurück?')) return; onCancel(); }} style={zurueckStil}>‹ Zurück</button>
          <div>
            <div style={{ color: C.white, fontWeight: 800, fontSize: 16 }}>{isEditMode ? 'Station bearbeiten' : '+ Neue Trafostation'}</div>
            {kunde && <div style={{ color: C.gray300, fontSize: 14 }}>{isEditMode ? 'Kunde: ' : 'für '}{kunde.name}</div>}
          </div>
        </div>
      </div>

      <div style={{ background: C.white, borderBottom: '1px solid ' + C.gray200, position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ maxWidth: 1400, margin: '0 auto', display: 'flex', overflowX: 'auto' }}>
          {(isBuero ? [...TABS, TAB_VERTRAEGE] : TABS).map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
              flex: '1 0 auto', padding: '14px 14px', background: tabBg(tab.key), color: tabColor(tab.key),
              border: 'none', borderBottom: tabBorder(tab.key), cursor: 'pointer',
              fontSize: 15, fontWeight: tabWeight(tab.key), whiteSpace: 'nowrap', minWidth: 110,
            }}>{tab.label}</button>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '20px 14px 110px' }}>

        {/* === ANSCHRIFTEN === */}
        {activeTab === 'anschrift' && (
          <div style={{ background: C.white, borderRadius: 10, padding: 22, boxShadow: '0 1px 6px rgba(10,22,40,0.06)' }}>
            <Subsection title='Auftraggeber (Kunde)'>
              {kunde ? (
                <div style={{ background: C.gray100, borderRadius: 8, padding: 14, fontSize: 15 }}>
                  <div style={{ fontWeight: 800, color: C.navy, fontSize: 14, marginBottom: 4 }}>{kunde.name}</div>
                  <div style={{ color: C.gray700 }}>{[kunde.strasse, [kunde.plz, kunde.ort].filter(Boolean).join(' ')].filter(Boolean).join(', ') || '(keine Anschrift)'}</div>
                  {kunde.ansprechpartner && <div style={{ color: C.gray500, marginTop: 4 }}>{kunde.ansprechpartner}{kunde.position ? ' (' + kunde.position + ')' : ''}{kunde.telefon ? ' - ' + kunde.telefon : ''}{kunde.mobil ? ' - Mobil ' + kunde.mobil : ''}{kunde.email ? ' - ' + kunde.email : ''}</div>}
                  <div style={{ marginTop: 8, fontSize: 13, color: C.gray500, fontStyle: 'italic' }}>(Auftraggeber-Daten in den Kunden-Stammdaten bearbeiten)</div>
                </div>
              ) : (kundeLoading || (effectiveKundeId && !kunde)) ? (
                <div style={{ padding: 14, color: C.gray500, fontStyle: 'italic' }}>Lade Kundendaten...</div>
              ) : (
                <div style={{ padding: 14, color: C.gray500, fontStyle: 'italic' }}>Kein Kunde verknüpft.</div>
              )}
            </Subsection>

            <Subsection title='Objekt-Anschrift'>
              <Checkbox
                checked={stammdaten.objekt_abweichend}
                onChange={v => updateStamm('objekt_abweichend', v)}
                label='Objekt-Anschrift weicht vom Auftraggeber ab'/>
              {stammdaten.objekt_abweichend && (
                <div style={{ marginTop: 10, padding: 14, background: C.yellowBg, borderRadius: 8 }}>
                  <Field label='Objekt-Name'><TextInput value={stammdaten.objekt_name} onChange={v => updateStamm('objekt_name', v)} placeholder='z.B. Wohnpark Mustermann oder Hausverwaltung XYZ'/></Field>
                  <Row columns={3}>
                    <Field label='Strasse'><TextInput value={stammdaten.objekt_strasse} onChange={v => updateStamm('objekt_strasse', v)}/></Field>
                    <Field label='PLZ'><TextInput value={stammdaten.objekt_plz} onChange={v => updateStamm('objekt_plz', v)}/></Field>
                    <Field label='Ort'><TextInput value={stammdaten.objekt_ort} onChange={v => updateStamm('objekt_ort', v)}/></Field>
                  </Row>
                  <Row columns={2}>
                    <Field label='Telefon Zentrale'><TextInput value={stammdaten.objekt_telefon} onChange={v => updateStamm('objekt_telefon', v)}/></Field>
                    <Field label='E-Mail'><TextInput value={stammdaten.objekt_email} onChange={v => updateStamm('objekt_email', v)} type='email'/></Field>
                  </Row>
                  <Row columns={2}>
                    <Field label='Ansprechpartner'><TextInput value={stammdaten.objekt_ansprechpartner} onChange={v => updateStamm('objekt_ansprechpartner', v)}/></Field>
                    <Field label='Position'><TextInput value={stammdaten.objekt_position} onChange={v => updateStamm('objekt_position', v)}/></Field>
                  </Row>
                  <Row columns={2}>
                    <Field label='Telefon Durchwahl'><TextInput value={stammdaten.objekt_telefon_durchwahl} onChange={v => updateStamm('objekt_telefon_durchwahl', v)}/></Field>
                    <Field label='Mobil'><TextInput value={stammdaten.objekt_mobil} onChange={v => updateStamm('objekt_mobil', v)}/></Field>
                  </Row>
                </div>
              )}
            </Subsection>

            <Subsection title='Ansprechpartner vor Ort'>
              <div style={{ fontSize: 13, color: C.gray500, marginBottom: 10, fontStyle: 'italic' }}>Optional - wer ist auf der Baustelle erreichbar? Kann anders sein als der Kunden-/Objekt-Ansprechpartner.</div>
              <Row columns={2}>
                <Field label='Name'><TextInput value={stammdaten.vor_ort_ansprechpartner} onChange={v => updateStamm('vor_ort_ansprechpartner', v)}/></Field>
                <Field label='Position'><TextInput value={stammdaten.vor_ort_position} onChange={v => updateStamm('vor_ort_position', v)} placeholder='z.B. Hausmeister'/></Field>
              </Row>
              <Row columns={3}>
                <Field label='Telefon'><TextInput value={stammdaten.vor_ort_telefon} onChange={v => updateStamm('vor_ort_telefon', v)}/></Field>
                <Field label='Mobil'><TextInput value={stammdaten.vor_ort_mobil} onChange={v => updateStamm('vor_ort_mobil', v)}/></Field>
                <Field label='E-Mail'><TextInput value={stammdaten.vor_ort_email} onChange={v => updateStamm('vor_ort_email', v)}/></Field>
              </Row>
            </Subsection>

            <Subsection title='Verteilnetzbetreiber (VNB)'>
              <Field label='VNB' hint='Suchen, auswählen oder mit "+ Neuer VNB" anlegen. Karlsruhe-Region als Favoriten ganz oben.'>
                <VnbPicker
                  vnben={vnben}
                  selectedId={stammdaten.vnb_id}
                  onChange={(id) => updateStamm('vnb_id', id)}
                  onCreated={() => reloadVnben()}
                />
              </Field>
              {selectedVnb && (
                <div style={{ marginTop: 8, padding: 14, background: C.gray100, borderRadius: 8, fontSize: 15, lineHeight: 1.6 }}>
                  <div style={{ fontWeight: 800, color: C.navy, fontSize: 14, marginBottom: 4 }}>{selectedVnb.name}</div>
                  <div style={{ color: C.gray700 }}>
                    {[selectedVnb.strasse, [selectedVnb.plz, selectedVnb.ort].filter(Boolean).join(' '), selectedVnb.bundesland].filter(Boolean).join(', ') || '(keine Anschrift)'}
                  </div>
                  {selectedVnb.telefon_zentrale && <div style={{ color: C.gray700 }}>Zentrale: {selectedVnb.telefon_zentrale}</div>}
                  {selectedVnb.email && <div style={{ color: C.gray700 }}>E-Mail: {selectedVnb.email}</div>}
                  {selectedVnb.webseite && <div><a href={selectedVnb.webseite} target='_blank' rel='noreferrer' style={{ color: C.navyMid }}>{selectedVnb.webseite}</a></div>}
                  {(selectedVnb.ansprechpartner || selectedVnb.telefon_durchwahl || selectedVnb.mobil || selectedVnb.ansprechpartner_email) && (
                    <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid ' + C.gray200 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: C.gray500, textTransform: 'uppercase', letterSpacing: 0.4 }}>Ansprechpartner</div>
                      {selectedVnb.ansprechpartner && <div style={{ fontWeight: 700, color: C.navy }}>{selectedVnb.ansprechpartner}{selectedVnb.position ? ' (' + selectedVnb.position + ')' : ''}</div>}
                      <div style={{ color: C.gray700 }}>
                        {[selectedVnb.telefon_durchwahl && 'Tel. ' + selectedVnb.telefon_durchwahl, selectedVnb.mobil && 'Mobil ' + selectedVnb.mobil, selectedVnb.ansprechpartner_email].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                  )}
                  <div style={{ marginTop: 8, fontSize: 13, color: C.gray500, fontStyle: 'italic' }}>(VNB-Daten in „VNBs verwalten" anpassen)</div>
                </div>
              )}
              {/* 17.05. #48: Feld 'Stationsbezeichnung beim VNB' hier entfernt - doppelt zum Pflichtfeld 'Stationsname bei VNB' im Station-Tab. */}
            </Subsection>
          </div>
        )}

        {/* === STATION === */}
        {activeTab === 'station' && (
          <div style={{ background: C.white, borderRadius: 10, padding: 22, boxShadow: '0 1px 6px rgba(10,22,40,0.06)' }}>
            <Subsection title='Stations-Identifikation'>
              <Field label='Stationsname bei VNB' required hint='Wie der Netzbetreiber die Station nennt, z.B. "H1282". Bleibt auch bei Kundenwechsel gleich.'>
                <TextInput value={stammdaten.stations_nr} onChange={v => updateStamm('stations_nr', v)}/>
              </Field>
              <Field label='Bezeichnung' hint='Vorschlag antippen und frei ergänzen, z.B. "Unterstation Süd 1"'>
                <><BezeichnungVorschlaege value={stammdaten.bezeichnung} onChange={v => updateStamm('bezeichnung', v)}/>
                <TextInput value={stammdaten.bezeichnung} onChange={v => updateStamm('bezeichnung', v)}/></>
              </Field>
              {/* Bauform: schreibt jetzt in station_konfiguration.stations_typ - das
                  Feld, das Planung, Kalender und PDF lesen. Vorher schrieb es in
                  stationen.typ, das niemand ausser diesem Formular las (H1605). */}
              <Field label='Bauform'><Select value={config.stations_typ} onChange={v => updateConf('stations_typ', v || '')} options={BAUFORM}/></Field>
              {/* Raeume - daraus die Isoliermatten (05.7) und die Schilder (05.9). Nur bei
                  Kompaktstationen nicht. Vorher fehlten sie auch, solange keine Bauform
                  gewaehlt war - dann waren sie nicht zu finden (Anna-Lena 22.09.2026). */}
              {config.stations_typ !== 'kompakt' && (
                <div style={{ fontSize: 14, fontWeight: 700, color: C.navy, margin: '6px 0 2px' }}>Räume der Station</div>
              )}
              {config.stations_typ !== 'kompakt' && (
                <Row columns={4}>
                  {RAUM_FELDER.map(([feld, label]) => (
                    <Field key={feld} label={label}>
                      <Select value={config[feld] != null ? String(config[feld]) : ''} onChange={v => updateConf(feld, v === null || v === '' ? null : Number(v))} options={RAUM_ANZAHL}/>
                    </Field>
                  ))}
                </Row>
              )}
            </Subsection>

            <Subsection title='GPS-Koordinaten'>
              <div style={{ fontSize: 13, color: C.gray500, marginBottom: 10, fontStyle: 'italic' }}>Genaue Position der Station auf dem Grundstück. Adresse kommt aus dem Anschriften-Tab.</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
                <button onClick={handleUseLocation} type='button' style={{ padding: '8px 14px', background: C.navy, color: C.yellow, border: 'none', borderRadius: 7, cursor: 'pointer', fontWeight: 700, fontSize: 15 }}>Aktueller Standort verwenden</button>
                {(stammdaten.gps_lat !== null && stammdaten.gps_lng !== null) && <span style={{ fontSize: 14, color: C.green, fontWeight: 700 }}>OK {stammdaten.gps_lat}, {stammdaten.gps_lng}</span>}
                <button onClick={() => { updateStamm('gps_lat', null); updateStamm('gps_lng', null); }} type='button' style={{ padding: '6px 10px', background: 'none', color: C.gray500, border: '1px solid ' + C.gray200, borderRadius: 6, cursor: 'pointer', fontSize: 14 }}>zurücksetzen</button>
              </div>
              <Row columns={2}>
                <Field label='Breitengrad'><NumberInput value={stammdaten.gps_lat} onChange={v => updateStamm('gps_lat', v)} step={0.000001}/></Field>
                <Field label='Längengrad'><NumberInput value={stammdaten.gps_lng} onChange={v => updateStamm('gps_lng', v)} step={0.000001}/></Field>
              </Row>
            </Subsection>

            <Subsection title='Stationsgebäude'>
              <Row columns={2}>
                <Field label='Hersteller' hint='Auswählen oder eigenen eintippen'><ComboInput value={config.stations_hersteller} onChange={v => updateConf('stations_hersteller', v)} options={STATIONS_HERSTELLER_ALLE} placeholder='z. B. Gritec'/></Field>
                {/* Baureihen des gewaehlten Herstellers; ohne Hersteller die Gritec-Liste. */}
                <Field label='Typ / Modell' hint='Baureihen des Herstellers, sonst frei'><ComboInput value={config.stations_modelltyp} onChange={v => updateConf('stations_modelltyp', v)} options={config.stations_hersteller && stationsTypenFuer(config.stations_hersteller).length ? stationsTypenFuer(config.stations_hersteller) : STATIONS_TYPEN} placeholder='z. B. UK 2000-28'/></Field>
              </Row>
              <Row columns={2}>
                <Field label='Seriennummer'><TextInput value={config.stations_seriennummer} onChange={v => updateConf('stations_seriennummer', v)}/></Field>
                <Field label='Baujahr Stationsgebäude'><NumberInput value={config.stations_baujahr ?? stammdaten.baujahr} onChange={v => { updateConf('stations_baujahr', v); updateStamm('baujahr', v); }}/></Field>
              </Row>
            </Subsection>

            <Subsection title='Wartung'>
              <Row columns={2}>
                <Field label='Letzte Wartung' hint='Nur Monat + Jahr'>
                  <DateInput type='month' value={(stammdaten.letzte_wartung || '').slice(0, 7)} onChange={v => updateStamm('letzte_wartung', v ? v + '-01' : '')}/>
                </Field>
                <Field label='Wartungs-Zyklus'><Select value={stammdaten.wartungs_zyklus} onChange={v => updateStamm('wartungs_zyklus', v)} options={stammdaten.wartungs_zyklus && !WARTUNGSZYKLUS.some(o => o.value === stammdaten.wartungs_zyklus)
                  ? [{ value: stammdaten.wartungs_zyklus, label: stammdaten.wartungs_zyklus.replace('jaehrig', 'jährig') + ' (nicht mehr vorgesehen – bitte ändern)' }, ...WARTUNGSZYKLUS]
                  : WARTUNGSZYKLUS}/></Field>
              </Row>
            </Subsection>

            {/* Diese drei Haekchen hingen bis 20.09.2026 in zwei Reitern, die es
                nicht mehr gibt - sie waren also nirgends einstellbar. Folge: Die App
                hielt jede Station fuer "ohne Oelwanne" und schlug das auch so vor,
                und im PDF stand Thermografie immer auf "nein". */}
            <Subsection title='Ausstattung der Station'>
              <div style={{ fontSize: 14, color: C.gray500, marginBottom: 8, lineHeight: 1.45 }}>
                Bestimmt, welche Kapitel im Wartungsprotokoll erscheinen und was im PDF steht.
              </div>
              <Checkbox checked={config.nshv_vorhanden} onChange={v => updateConf('nshv_vorhanden', v)} label='NSHV (Niederspannungs-Hauptverteilung) vorhanden'/>
              {config.nshv_vorhanden && <VerteilungenEditor config={config} updateConf={updateConf}/>}
              <div style={{ height: 6 }}/>
              <Checkbox checked={config.oelauffangwanne_vorhanden} onChange={v => updateConf('oelauffangwanne_vorhanden', v)} label='Ölauffangwanne vorhanden (Maße werden pro Trafo erfasst)'/>
              <div style={{ height: 6 }}/>
              <Checkbox checked={config.elektrothermografie_durchgef} onChange={v => updateConf('elektrothermografie_durchgef', v)} label='Elektrothermografie wird durchgeführt'/>
            </Subsection>

            <Field label='Notizen zur Station'><TextInput value={stammdaten.notizen} onChange={v => updateStamm('notizen', v)}/></Field>
          </div>
        )}

        {/* === MS-ANLAGE === */}
        {activeTab === 'msanlage' && (
          <div style={{ background: C.white, borderRadius: 10, padding: 22, boxShadow: '0 1px 6px rgba(10,22,40,0.06)' }}>
            <Checkbox checked={config.ms_anlage_vorhanden} onChange={v => updateConf('ms_anlage_vorhanden', v)} label='MS-Anlage vorhanden'/>
            {config.ms_anlage_vorhanden ? (
              <>
                <Subsection title='Anlagen-Daten'>
                  <Row columns={2}>
                    <Field label='Hersteller' hint='Vorschlag wählen oder eigenen eintippen. Wird automatisch in alle MS-Felder ohne eigenen Hersteller übernommen.'>
                      <HerstellerPicker
                        value={config.ms_anlage_hersteller}
                        onChange={v => {
                          const old = config.ms_anlage_hersteller || '';
                          updateConf('ms_anlage_hersteller', v);
                          // Auto-Propagation: Alle MS-Felder, die leer sind oder noch
                          // den alten MS-Anlage-Hersteller hatten, mit neuem Wert befuellen.
                          // Felder mit individuellem Hersteller bleiben unangetastet.
                          setMsFelder(prev => prev.map(f => {
                            const fh = (f.hersteller || '').trim();
                            if (!fh || fh === old.trim()) {
                              return { ...f, hersteller: v };
                            }
                            return f;
                          }));
                        }}
                        options={MS_HERSTELLER_VORSCHLAEGE}
                      />
                    </Field>
                    <Field label='Anzahl Felder (nominell)'><NumberInput value={config.ms_anlage_anzahl_felder} onChange={v => updateConf('ms_anlage_anzahl_felder', v)}/></Field>
                  </Row>
                  <Row columns={2}>
                    <Field label='Betriebsspannung [kV]'><ComboInput value={config.ms_anlage_betriebsspannung_kv} onChange={v => updateConf('ms_anlage_betriebsspannung_kv', v)} options={SPANNUNG_OPTIONEN}/></Field>
                    <Field label='Isolierart' hint={(() => {
                      if (config.ms_anlage_isolierart) return undefined;
                      const feld = msFelder.find(f => f.typ);
                      const iso = feld ? isolierartFuerMsTyp(feld.hersteller || config.ms_anlage_hersteller, feld.typ) : null;
                      const text = ISOLIERART.find(o => o.value === iso)?.label;
                      return text ? 'Typ „' + feld.typ + '" ist üblicherweise ' + text + ' – bitte auswählen' : 'Steuert die Vorschläge beim Sicherheitszubehör';
                    })()}><Select value={config.ms_anlage_isolierart} onChange={v => updateConf('ms_anlage_isolierart', v)} options={ISOLIERART}/></Field>
                  </Row>
                  {/* Nur Luftanlagen: ohne Erdungsschalter braucht jeder Abgang eine E+K (05.10). */}
                  {(config.ms_anlage_isolierart === 'luft' || config.ms_anlage_isolierart === 'luft_offen') && (
                    <Row columns={2}>
                      <Field label='Erdungsschalter in den Feldern vorhanden'>
                        <Select value={config.ms_erdungsschalter === true ? 'ja' : config.ms_erdungsschalter === false ? 'nein' : ''}
                          onChange={v => updateConf('ms_erdungsschalter', v === 'ja' ? true : v === 'nein' ? false : null)}
                          options={[{ value: 'ja', label: 'ja' }, { value: 'nein', label: 'nein' }]}/>
                      </Field>
                    </Row>
                  )}
                </Subsection>
                <Subsection title={'MS-Felder (' + msFelder.length + ')'}>
                  <MsFelderEditor items={msFelder} setItems={setMsFelder} defaultHersteller={config.ms_anlage_hersteller}
                    stationId={internalStationId} darfHochladen={isBuero}/>
                </Subsection>
              </>
            ) : (
              <div style={{ marginTop: 14, padding: 14, color: C.gray500, fontStyle: 'italic', background: C.gray100, borderRadius: 8 }}>
                Keine MS-Anlage. Häkchen oben setzen, um Felder einzublenden.
              </div>
            )}
          </div>
        )}

        {/* === TRAFOS === */}
        {activeTab === 'trafos' && (
          <div style={{ background: C.white, borderRadius: 10, padding: 22, boxShadow: '0 1px 6px rgba(10,22,40,0.06)' }}>
            <div style={{ marginBottom: 14, fontWeight: 800, color: C.navy, fontSize: 16 }}>Transformatoren ({trafos.length})</div>
            <TrafosEditor items={trafos} setItems={setTrafos}/>
          </div>
        )}

        {/* === WANDLER === */}
        {activeTab === 'wandler' && (
          <div style={{ background: C.white, borderRadius: 10, padding: 22, boxShadow: '0 1px 6px rgba(10,22,40,0.06)' }}>
            <Subsection title='Verrechnungsmessung'>
              <Field label='Art der Messung'><Select value={config.verrechnungsmessung} onChange={v => updateConf('verrechnungsmessung', v)} options={VERRECHNUNG}/></Field>
              {/* Die Haekchen "Strom-/Spannungswandler vorhanden" sind weg (Anna-Lena
                  18.09.2026: doppelt). Ob es Wandler gibt, zeigt die Liste darunter. */}
              {config.verrechnungsmessung === 'ns' && (
                <div style={{ fontSize: 14, color: C.gray500 }}>NS-Messung: Es gibt nur Stromwandler.</div>
              )}
            </Subsection>
            <Subsection title={'Wandler (' + wandler.length + ')'}>
              <WandlerEditor items={wandler} setItems={setWandler} nurStrom={config.verrechnungsmessung === 'ns'}/>
            </Subsection>
            {/* Zaehler (Anna-Lena 08.10.2026, Migration 76) */}
            <Subsection title={'Zähler (' + (config.zaehler || []).length + ')'}>
              <ZaehlerEditor items={config.zaehler} onChange={v => updateConf('zaehler', v)} wandler={wandler}/>
            </Subsection>
          </div>
        )}

        {/* === VERTRAEGE === (eigener Speichern-Knopf, unabhaengig vom Stationsformular) */}
        {/* Bleibt nach dem ersten Oeffnen eingehaengt (nur versteckt), sonst waeren
            ungespeicherte Vertragsangaben beim Reiterwechsel weg. */}
        {vertraegeBesucht.current && isBuero && (
          <div style={{ display: activeTab === 'vertraege' ? 'block' : 'none', maxWidth: 1100 }}>
            <div style={{ fontSize: 13, color: C.gray500, marginBottom: 10 }}>
              Verträge haben einen eigenen „Speichern“-Knopf – unabhängig vom Speichern der Station.
            </div>
            <VertraegeEditor stationId={internalStationId} onAenderungen={(x) => { vertraegeUngespeichert.current = x; }}/>
          </div>
        )}

        {/* === ERDERANLAGE === */}
        {activeTab === 'erderanlage' && (
          <div style={{ background: C.white, borderRadius: 10, padding: 22, boxShadow: '0 1px 6px rgba(10,22,40,0.06)' }}>
            <Checkbox checked={config.erderanlage_vorhanden} onChange={v => updateConf('erderanlage_vorhanden', v)} label='Erderanlage vorhanden'/>
            {!config.erderanlage_vorhanden && (
              <div style={{ marginTop: 14, padding: 14, color: C.gray500, fontStyle: 'italic', background: C.gray100, borderRadius: 8 }}>
                Keine Erderanlage. Häkchen oben setzen, falls vorhanden.
              </div>
            )}
            {config.erderanlage_vorhanden && (
              <Subsection title='Erderanlage – Schienen und Anschlüsse'>
                <div style={{ fontSize: 14, color: C.gray700, marginBottom: 12, lineHeight: 1.45 }}>
                  Hier werden die Schienen und ihre Anschlüsse angelegt — zum Beispiel im
                  Büro anhand von Fotos, damit die Monteure sie vor Ort schon vorfinden.
                  <strong> Die Ohm-Werte werden bei jeder Wartung neu gemessen</strong> und
                  stehen deshalb im Protokoll, nicht hier.
                </div>
                {/* internalStationId, nicht der Seiten-Parameter stationId: Bei einer
                    NEUEN Station ist stationId leer und bleibt es auch nach dem
                    Speichern - erst internalStationId kennt die neue Nummer. Vorher
                    stand hier deshalb dauerhaft "Bitte zuerst speichern". */}
                <ErderanlageBlock stationId={internalStationId} grenzeOhm={config.erder_grenzwert_ohm} grenzeAendern={(v) => updateConf('erder_grenzwert_ohm', v)} />
              </Subsection>
            )}
          </div>
        )}

        {error && <div style={{ padding: 14, background: C.redBg, color: C.red, borderRadius: 8, marginTop: 14, fontSize: 15 }}>! {error}</div>}
        {progress && !error && <div style={{ padding: 14, background: C.yellowBg, color: C.navy, borderRadius: 8, marginTop: 14, fontSize: 15 }}>{progress}</div>}
      </div>
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: C.white, borderTop: '1px solid ' + C.gray200, boxShadow: '0 -2px 14px rgba(0,0,0,0.08)', padding: '12px 14px', zIndex: 100 }}>
        <div style={{ maxWidth: 1400, margin: '0 auto', display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center' }}>
          <button onClick={abbrechen} disabled={saving} style={{ padding: '10px 16px', background: C.gray100, color: C.navy, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 15 }}>Abbrechen</button>
          <button onClick={() => handleSave(false)} disabled={saving} style={{ padding: '10px 16px', background: saving ? C.gray300 : C.navyMid, color: C.yellow, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 15 }}>Zwischenspeichern</button>
          <button onClick={() => handleSave(true)} disabled={saving} style={{ padding: '10px 22px', background: saving ? C.gray300 : C.yellow, color: C.navy, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 800, fontSize: 14 }}>{saving ? 'Speichert...' : 'Fertigstellen'}</button>
        </div>
      </div>
    </div>
  );
}
